import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { defineMessages, useIntl } from "react-intl";
import SpinnerLoader from "@app/neva/components/spinnerLoader/spinnerLoader";
import { ConfigContext, IntlContext } from "@app/provider";
import { getGisScheduleLink } from "@app/utils/getGisScheduleLink";
import { ParsedTestCard, TestType } from "@app/provider/testData/types";
import IntakeFormService, {
  CreateOncUppGisTriageResponse,
} from "@app/service/intakeForm";
import { routes } from "@app/routing";
import {
  buildAcuityGisIframeUrl,
  getAcuityAppointmentTypeId,
  getAcuityOwnerConfig,
} from "@app/pages/private/scheduleAGeneticInformationSession/buildAcuityUrl";
import {
  clearGisReturnUrl,
  classifyCounselingIntent,
  CounselingIntent,
  getGisEntrySource,
  GIS_ENTRY_SOURCE_PUBLIC,
  GIS_NO_ORDER_UID,
  storeGisReturnUrl,
} from "@app/utils/gisEntrySource";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import { useEmbeddedFrameBack } from "@app/hooks";
import { redirectTo } from "@app/utils";

import {
  GIS_SELECTED_ORDER_PARAM,
  useEligibleGisOrders,
} from "./useEligibleGisOrders";

import "./publicGisTriage.scss";

/**
 * Routing matrix — Work Item 3 (Public GIS Triage).
 *
 * After a public-entry visitor authenticates (via `PublicGisEntry`), they
 * land here. The triage page auto-selects the patient's single GIS-eligible
 * order and routes it to the correct downstream GIS experience based on the
 * order's product (test) type.
 *
 * The split mirrors the two existing routing-rule sources of truth:
 *   - Client:  `getAcuityOwnerConfig` in
 *              `client/src/pages/private/scheduleAGeneticInformationSession/buildAcuityUrl.ts`
 *   - Server:  `ACTION_TO_FLOW` in
 *              `server/src/service/gisIntakeFormService/GisTriageRulesEngine.ts`
 *
 *   | Product type                          | Flow             | Acuity owner | Appointment        |
 *   |---------------------------------------|------------------|--------------|--------------------|
 *   | SIGNATERA, ALTERA, LATITUDE, DPYD     | ONC UPP GIS      | 27869891     | ONC GIS (ID 39679197) |
 *   | EMPOWER (+ any other WH/OH product)   | WH Acuity iframe | 11361661     | WH GIS (Empower result-aware) |
 *
 * Both branches reuse the field IDs returned by `getAcuityOwnerConfig` so
 * Acuity's per-calendar "scheduling source" and "counseling intent" fields
 * remain consistent with the legacy entry points (test-result page, etc.).
 *
 * Edge-case handling (Work Item 3 — error handling sub-ticket):
 *   - No eligible orders → "no orders" empty state.
 *   - Multiple eligible orders → redirect to the `PublicGisOrderSelect` picker
 *     so the patient can choose which order to schedule. The picker routes the
 *     chosen order back here via the `orderUid` query param.
 *   - WH/OH product with no Acuity mapping → "unsupported" message.
 *   - Order fetch throws / returns non-array → "load error" with Retry.
 *   - Cancelled / expired tests are excluded from the eligible-order count
 *     via `isGisEligibleStatus`.
 *   - GIS return URL is (re)stored on mount so a session timeout mid-flow
 *     replays back to this triage page after re-authentication.
 */

const messages = defineMessages({
  pageTitle: {
    id: "publicGisTriage.pageTitle",
    defaultMessage: "Schedule Your Genetic Information Session",
  },
  unsupportedTitle: {
    id: "publicGisTriage.unsupportedTitle",
    defaultMessage: "Genetic Information Session not available",
  },
  unsupportedBody: {
    id: "publicGisTriage.unsupportedBody",
    defaultMessage:
      "A Genetic Information Session isn't available for this test type. Please contact your healthcare provider or return to the portal home.",
  },
  loadErrorTitle: {
    id: "publicGisTriage.loadErrorTitle",
    defaultMessage: "We couldn't load your orders",
  },
  loadErrorBody: {
    id: "publicGisTriage.loadErrorBody",
    defaultMessage:
      "Something went wrong while loading your orders. Please try again.",
  },
  sessionErrorTitle: {
    id: "publicGisTriage.sessionErrorTitle",
    defaultMessage: "We couldn't start your scheduling session",
  },
  sessionErrorBody: {
    id: "publicGisTriage.sessionErrorBody",
    defaultMessage:
      "Something went wrong while preparing your Genetic Information Session. Please contact support to schedule manually.",
  },
  retry: {
    id: "publicGisTriage.retry",
    defaultMessage: "Try again",
  },
  backToHome: {
    id: "publicGisTriage.backToHome",
    defaultMessage: "Back to home",
  },
  back: {
    id: "publicGisTriage.back",
    defaultMessage: "Back",
  },
  iframeLoadingLabel: {
    id: "publicGisTriage.iframeLoadingLabel",
    defaultMessage: "Loading scheduling form…",
  },
});

/**
 * Builds the WH/OH Acuity iframe URL for the auto-selected order.
 * Delegates the owner / appointment-type / field-ID lookups to the shared
 * `buildAcuityGisIframeUrl` helper so this page emits the same Acuity
 * tracking fields as the other iframe entry points.
 */
function buildWhIframeUrl(card: ParsedTestCard): string {
  return buildAcuityGisIframeUrl({
    testType: card.testType,
    status: card.status,
    resultCode: card.resultCode,
    schedulingSourceValue: GIS_ENTRY_SOURCE_PUBLIC,
  });
}

type Flow = "oncology" | "wh" | "unsupported";

function resolveFlow(card: ParsedTestCard): Flow {
  const isOncology =
    getAcuityOwnerConfig(card.testType).owner === "27869891" &&
    card.testType !== TestType.EMPOWER;
  if (isOncology) return "oncology";
  // WH/OH flow needs a valid Acuity appointment-type mapping to render the
  // iframe; if none exists for this product, surface the "unsupported"
  // state instead of a half-built scheduler URL.
  if (!getAcuityAppointmentTypeId(card.testType)) return "unsupported";
  return "wh";
}

const PublicGisTriage: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { config } = useContext(ConfigContext);
  const { currentLanguage } = useContext(IntlContext);
  const { eligibleOrders, isLoading, loadError, retry } =
    useEligibleGisOrders();

  const [iframeLoading, setIframeLoading] = useState(true);

  // The intake flow cannot complete without the `OncUppGisTriage` row (the
  // consent step throws "No active GIS intake session found" without it), so a
  // failed mutation must block navigation and surface an explicit error rather
  // than silently dropping the patient into a dead-end intake funnel.
  const [sessionCreateFailed, setSessionCreateFailed] = useState(false);

  const [createGisTriageSession] = useMutation<CreateOncUppGisTriageResponse>(
    IntakeFormService.createOncUppGisTriage()
  );

  // Persist the GIS return URL whenever this page mounts so that if the
  // user's session times out mid-flow (e.g. while the WH iframe is open or
  // partway through the oncology intake form), re-authentication can replay
  // them back to this triage page rather than dumping them on a generic
  // landing.
  useEffect(() => {
    storeGisReturnUrl(routes.gisTriage);
    heap.track(HEAP_EVENTS.upp_view_gis_triage, {
      location: HeapEventLocation.gis_triage_page,
      source: GIS_ENTRY_SOURCE_PUBLIC,
    });
  }, []);

  // A patient with more than one eligible order picks one in
  // `PublicGisOrderSelect`, which routes back here with `?orderUid=<uid>`.
  // When that param matches an eligible order we treat it as the selection;
  // otherwise we only auto-select when exactly one eligible order exists.
  const requestedOrderUid = searchParams.get(GIS_SELECTED_ORDER_PARAM);
  const selectedOrder = useMemo<ParsedTestCard | null>(() => {
    if (requestedOrderUid) {
      const match = eligibleOrders.find((o) => o.uid === requestedOrderUid);
      // Fall through to the single-order rule if the requested uid is stale
      // (e.g. the order is no longer eligible) so we don't strand the patient.
      if (match) return match;
    }
    return eligibleOrders.length === 1 ? eligibleOrders[0] : null;
  }, [eligibleOrders, requestedOrderUid]);

  // More than one eligible order and nothing valid pre-selected → send the
  // patient to the picker rather than silently choosing one for them.
  const needsOrderSelection =
    !isLoading && !loadError && eligibleOrders.length > 1 && !selectedOrder;
  const orderSelectRedirectRef = useRef(false);
  useEffect(() => {
    if (needsOrderSelection && !orderSelectRedirectRef.current) {
      orderSelectRedirectRef.current = true;
      navigate(routes.gisOrderSelect, { replace: true });
    }
  }, [needsOrderSelection, navigate]);

  // Renasight is GIS-eligible (it appears in the order picker), but unlike the
  // oncology / WH products it is scheduled via the Renasight-specific external
  // scheduling page rather than the in-app intake flow or WH Acuity iframe.
  // Whenever the resolved order is Renasight — whether auto-selected as the
  // patient's single eligible order or chosen from the multi-order picker — we
  // redirect there. This mirrors how the test-result detail page's "Schedule a
  // GIS" button routes Renasight via `getGisScheduleLink`.
  const renasightRedirect =
    !isLoading && !loadError && selectedOrder?.testType === TestType.RENASIGHT;
  const renasightRedirectRef = useRef(false);
  useEffect(() => {
    if (renasightRedirect && !renasightRedirectRef.current) {
      renasightRedirectRef.current = true;
      heap.track(HEAP_EVENTS.upp_click_gis, {
        location: HeapEventLocation.gis_triage_page,
        test_type: TestType.RENASIGHT,
        source: GIS_ENTRY_SOURCE_PUBLIC,
      });
      // Full-page redirect (rather than `openNewWindow`) because this runs in
      // an effect, not a user click — browsers block popups opened outside a
      // user gesture, and the patient came here specifically to schedule.
      redirectTo(
        getGisScheduleLink(TestType.RENASIGHT, config, currentLanguage)
      );
    }
  }, [renasightRedirect, config, currentLanguage]);

  // No GIS-eligible orders at all (none on file, or only cancelled/expired) →
  // the patient can still schedule a session. Route them into the ONC intake
  // flow using sentinel order/test uids so the confirm-info step renders the
  // "we couldn't find a test order" messaging and the funnel proceeds without
  // a backing order.
  const noEligibleOrders =
    !isLoading && !loadError && eligibleOrders.length === 0;
  const noOrderRedirectRef = useRef(false);
  useEffect(() => {
    if (noEligibleOrders && !noOrderRedirectRef.current) {
      noOrderRedirectRef.current = true;
      // Create the triage row up-front (as the oncology branch does) so the
      // downstream intake steps can attach data to it and the funnel analytics
      // are recorded. The row is persisted with NULL order/test ids server-side
      // (the sentinel is normalized away); prefill is sourced from the
      // patient's profile. Navigation is gated on success because the intake
      // funnel dead-ends at the consent step without this row.
      createGisTriageSession({
        variables: {
          orderUidPublic: GIS_NO_ORDER_UID,
          testUid: GIS_NO_ORDER_UID,
          schedulingSource: getGisEntrySource() ?? GIS_ENTRY_SOURCE_PUBLIC,
        },
      })
        .then(() => {
          navigate(
            routes.scheduleAGeneticInformationSessionIntakePage(
              GIS_NO_ORDER_UID,
              GIS_NO_ORDER_UID
            ),
            { replace: true }
          );
        })
        .catch((err) => {
          console.error("[GIS] failed to create no-order triage session", err);
          setSessionCreateFailed(true);
        });
    }
  }, [noEligibleOrders, navigate, createGisTriageSession]);

  const flow = useMemo<Flow | null>(
    () => (selectedOrder ? resolveFlow(selectedOrder) : null),
    [selectedOrder]
  );

  const counselingIntent = useMemo<CounselingIntent | null>(
    () =>
      selectedOrder ? classifyCounselingIntent(selectedOrder.status) : null,
    [selectedOrder]
  );

  // Fire the pre/post-test classification event once per resolved
  // single order. This is intentionally separate from the auto-route
  // event so analytics can break down classification distribution
  // even for orders that fall through to the WH iframe / unsupported
  // states (which don't auto-navigate).
  const classificationLoggedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedOrder || !counselingIntent) return;
    // Renasight redirects to its own external scheduling page, so it doesn't
    // terminate in any of the in-app flows this classification breaks down.
    if (renasightRedirect) return;
    if (classificationLoggedRef.current === selectedOrder.uid) return;
    classificationLoggedRef.current = selectedOrder.uid;
    heap.track(HEAP_EVENTS.upp_gis_triage_classification, {
      location: HeapEventLocation.gis_triage_page,
      source: GIS_ENTRY_SOURCE_PUBLIC,
      counseling_intent: counselingIntent,
      test_type: selectedOrder.testType,
      status: selectedOrder.status,
    });
  }, [selectedOrder, counselingIntent, renasightRedirect]);

  // Fire the single-order auto-route event once per resolved flow so
  // analytics can measure how often the public funnel terminates in
  // each downstream experience (oncology intake vs WH iframe vs
  // unsupported).
  const autoRouteLoggedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedOrder || !flow) return;
    // Renasight has its own redirect + `upp_click_gis` event; it doesn't
    // auto-route into oncology / WH / unsupported, so skip the auto-route event.
    if (renasightRedirect) return;
    const key = `${selectedOrder.uid}:${flow}`;
    if (autoRouteLoggedRef.current === key) return;
    autoRouteLoggedRef.current = key;
    heap.track(HEAP_EVENTS.upp_gis_triage_autoroute, {
      location: HeapEventLocation.gis_triage_page,
      source: GIS_ENTRY_SOURCE_PUBLIC,
      flow,
      test_type: selectedOrder.testType,
      counseling_intent: counselingIntent,
    });
  }, [flow, selectedOrder, counselingIntent, renasightRedirect]);

  // Public-entry equivalent of the legacy "Schedule a GIS" click on the
  // test-result page: create the `OncUppGisTriage` row up-front so each
  // downstream intake step (confirm-info → … → consent) can attach data to
  // it. Without this the intake flow throws "No active GIS intake session
  // found" at the consent step and the funnel analytics (gis_schedule_clicked,
  // intake_started, intake_completed, triage_result) are never recorded for
  // public-entry patients. Guarded by a ref so the row is created once per
  // resolved order; fire-and-forget mirrors the legacy entry point.
  const sessionCreatedRef = useRef<string | null>(null);
  useEffect(() => {
    if (flow !== "oncology" || !selectedOrder) return;
    if (sessionCreatedRef.current === selectedOrder.uid) return;
    sessionCreatedRef.current = selectedOrder.uid;
    // Navigation is gated on a successful mutation: the intake funnel dead-ends
    // at the consent step ("No active GIS intake session found") without this
    // row, so a failure must surface an error instead of silently advancing.
    createGisTriageSession({
      variables: {
        orderUidPublic: selectedOrder.orderUidPublic || "",
        testUid: selectedOrder.uid || "",
        schedulingSource: getGisEntrySource() ?? GIS_ENTRY_SOURCE_PUBLIC,
      },
    })
      .then(() => {
        navigate(
          routes.scheduleAGeneticInformationSessionIntakePage(
            selectedOrder.orderUidPublic,
            selectedOrder.uid
          ),
          { replace: true }
        );
      })
      .catch((err) => {
        console.error("[GIS] failed to create public triage session", err);
        setSessionCreateFailed(true);
      });
  }, [flow, selectedOrder, navigate, createGisTriageSession]);

  // Acuity answers Back with its own scheduling steps from inside the frame,
  // so Back off the scheduler is handed back to this page, which returns the
  // patient to whichever screen sent them here.
  const handleBack = useEmbeddedFrameBack(
    flow === "wh" && !isLoading && !loadError && !sessionCreateFailed
  );

  const handleBackToHome = useCallback(() => {
    // User is leaving the GIS flow on purpose — drop the stored return URL
    // so a later re-auth doesn't bounce them back into triage unexpectedly.
    clearGisReturnUrl();
    navigate(routes.home);
  }, [navigate]);

  // The triage row failed to create, so the intake funnel can't complete.
  // Surface an explicit error (no auto-retry) directing the patient to support
  // for manual scheduling rather than dropping them into a dead-end flow.
  if (sessionCreateFailed) {
    return (
      <div className="public-gis-triage">
        <div
          className="public-gis-triage__empty"
          role="alert"
          aria-live="polite"
        >
          <h1 className="public-gis-triage__empty-title">
            {intl.formatMessage(messages.sessionErrorTitle)}
          </h1>
          <p className="public-gis-triage__empty-body">
            {intl.formatMessage(messages.sessionErrorBody)}
          </p>
          <button
            type="button"
            className="public-gis-triage__back-btn"
            onClick={handleBackToHome}
          >
            {intl.formatMessage(messages.backToHome)}
          </button>
        </div>
      </div>
    );
  }

  // Keep the loader up while we bounce a multi-order patient to the picker (or
  // a no-order patient to the intake flow) so they never flash the single-order
  // UI.
  if (
    isLoading ||
    flow === "oncology" ||
    needsOrderSelection ||
    noEligibleOrders ||
    renasightRedirect
  ) {
    return (
      <div className="public-gis-triage">
        <div className="public-gis-triage__loader">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="public-gis-triage">
        <div
          className="public-gis-triage__empty"
          role="alert"
          aria-live="polite"
        >
          <h1 className="public-gis-triage__empty-title">
            {intl.formatMessage(messages.loadErrorTitle)}
          </h1>
          <p className="public-gis-triage__empty-body">
            {intl.formatMessage(messages.loadErrorBody)}
          </p>
          <div className="public-gis-triage__actions">
            <button
              type="button"
              className="public-gis-triage__primary-btn"
              onClick={retry}
            >
              {intl.formatMessage(messages.retry)}
            </button>
            <button
              type="button"
              className="public-gis-triage__back-btn"
              onClick={handleBackToHome}
            >
              {intl.formatMessage(messages.backToHome)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Defensive: with no eligible order we redirect (effect above) — keep the
  // loader up rather than rendering a half-built scheduler for a null order.
  if (!selectedOrder) {
    return (
      <div className="public-gis-triage">
        <div className="public-gis-triage__loader">
          <SpinnerLoader />
        </div>
      </div>
    );
  }

  if (flow === "unsupported") {
    return (
      <div className="public-gis-triage">
        <div className="public-gis-triage__empty">
          <h1 className="public-gis-triage__empty-title">
            {intl.formatMessage(messages.unsupportedTitle)}
          </h1>
          <p className="public-gis-triage__empty-body">
            {intl.formatMessage(messages.unsupportedBody)}
          </p>
          <button
            type="button"
            className="public-gis-triage__back-btn"
            onClick={handleBackToHome}
          >
            {intl.formatMessage(messages.backToHome)}
          </button>
        </div>
      </div>
    );
  }

  const iframeSrc = buildWhIframeUrl(selectedOrder);

  return (
    <div className="public-gis-triage">
      <div className="public-gis-triage__iframe-wrapper">
        <h1 className="public-gis-triage__title">
          {intl.formatMessage(messages.pageTitle)}
        </h1>
        {iframeLoading && (
          <div className="public-gis-triage__iframe-loader">
            <SpinnerLoader />
          </div>
        )}
        <iframe
          title={intl.formatMessage(messages.iframeLoadingLabel)}
          src={iframeSrc}
          width="100%"
          height="800"
          frameBorder="0"
          allow="payment"
          onLoad={() => setIframeLoading(false)}
          style={{ display: iframeLoading ? "none" : "block" }}
        />
        <script
          src="https://embed.acuityscheduling.com/js/embed.js"
          type="text/javascript"
        />
        <button
          type="button"
          className="public-gis-triage__back-btn"
          onClick={handleBack}
        >
          {intl.formatMessage(messages.back)}
        </button>
      </div>
    </div>
  );
};

export default PublicGisTriage;
